import { type AddTaskProps } from '@/components/Modals/AddTask/AddTask.model.ts';
import { StudyExecutionTimezone } from '@/components/Studies/Settings/Steps/Tasks/StudyExecutionTimezone.tsx';
import { taskTypes } from '@/components/Studies/Tasks/Task';
import Button from '@/components/UI/Button';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { classNames, errorToast, successToast, warningToast } from '@/helpers';
import { _isEmpty } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import type { Study } from '@/model/Study.model';
import { type TaskSpec, type TaskSpecApiId, type TaskSpecCreate, type TaskSpecUpdate } from '@/model/Task.model';
import { type Treatment } from '@/model/Treatment.model.ts';
import { ApiService } from '@/support/ApiService';
import { useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState.ts';
import { LegacyApiService } from '@/support/LegacyApiService.ts';
import { RouteService } from '@/support/RouteService';
import { featuresSelector } from '@/support/Selectors.tsx';
import { isClosed } from '@/support/study';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { modalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import { type FC, type MouseEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaskScheduleFrequencyCell } from '../../Settings.utils';
import { TaskSpecTimeCell } from '@/components/Tasks/TaskSpecTimeCell/TaskSpecTimeCell';
import { getTargetNameForTasks } from '@/components/Tasks/Show/Tasks.utils.ts';

interface TasksV2Props {
  study: Study;
  createTaskEnabled: boolean;
  reloadStudy?: () => Promise<void>;
}

const selector = createSelector([featuresSelector], (features) => ({
  studyExecutionTimezoneEnabled: features?.study_execution_timezone ?? false,
}));

type GlossaryData = {
  samples: Array<TeamGlossaryV1>;
  observations: Array<TeamGlossaryV1>;
  treatments: Array<Treatment>;
};
export const TasksV2: FC<TasksV2Props> = ({ study, createTaskEnabled, reloadStudy }) => {
  const { openModal, closeModal } = modalAction(useDispatch());
  const abortController = useAbortController();
  const [listApiVersion, setListApiVersion] = useState<number>(() => Date.now());
  const [glossaryData, updateGlossaryData] = useState<GlossaryData>(() => ({
    samples: [],
    observations: [],
    treatments: [],
  }));
  const { api_id: studyId, study_groups: studyGroups = [] } = study;
  const studyInactive = isClosed(study);
  const isMounted = useMountedState();
  const { studyExecutionTimezoneEnabled } = useSelector(selector);

  useEffect(() => {
    const onError = { toast: false, capture: true, throw: false };

    Promise.all([
      ApiService.call({
        endpoint: 'GET /api/v1/team-glossary',
        path: undefined,
        query: {
          group: 'samples',
          perPage: -1,
          filterType: 'and',
          'filters[]': 'status|eq|active',
          order: 'asc',
        },
        options: { onError: { ...onError, slug: 'samples-glossary-load' } },
      }),
      ApiService.call({
        endpoint: 'GET /api/v1/team-glossary',
        path: undefined,
        query: {
          group: 'observations',
          perPage: -1,
          filterType: 'and',
          'filters[]': 'status|eq|active',
          order: 'asc',
        },
        options: { onError: { ...onError, slug: 'observations-glossary-load' } },
      }),
      LegacyApiService.call({
        method: 'GET',
        apiRoute: 'studies.treatments',
        path: { studyId: study.id },
        query: { perPage: -1 },
        options: { onError: { ...onError, slug: 'study-treatments-load' } },
      }),
    ])
      .then(([samplesResponse, observationsResponse, treatmentsResponse]) => {
        if (isMounted()) {
          const samples: Array<TeamGlossaryV1> = (samplesResponse.body as { data?: Array<TeamGlossaryV1> })?.data ?? [];
          const observations: Array<TeamGlossaryV1> =
            (observationsResponse.body as { data?: Array<TeamGlossaryV1> })?.data ?? [];
          const treatments: Array<Treatment> = (treatmentsResponse.body as { data?: Array<Treatment> })?.data ?? [];
          updateGlossaryData({ samples, observations, treatments });
        }
      })
      .catch((cause) => {
        ExceptionHandler.captureException(
          new InVivoError('Could not load sample & observation glossary', {
            cause: cause,
            slug: 'tasks-glossary-load',
          })
        );
      });
  }, [isMounted, updateGlossaryData]);

  const handleTaskSpecCreate = async (taskSpec: TaskSpecCreate) => {
    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/studies/{studyId}/task-specs',
        body: [taskSpec],
        path: { studyId },
        signal: abortController.newAbortController().signal,
        options: { onError: { toast: false } },
      });
      setListApiVersion(new Date().getTime());
      successToast('Successfully created task');
    } catch (e) {
      errorToast('Could not create task');
      throw e;
    }
  };
  const handleTaskSpecUpdate = (taskSpecId: TaskSpecApiId) => {
    return async (taskSpec: TaskSpecUpdate) => {
      try {
        await ApiService.call({
          endpoint: 'PATCH /api/v1/studies/{studyId}/task-specs/{taskSpecId}',
          body: taskSpec,
          path: { studyId, taskSpecId },
          signal: abortController.newAbortController().signal,
          options: { onError: { toast: false } },
        });
        setListApiVersion(new Date().getTime());
        successToast('Successfully updated task');
      } catch (err) {
        errorToast('Could not update task');
        throw err;
      }
    };
  };

  const handleAddClick = () => {
    const execution: AddTaskProps['execution'] = {
      measurements: (study.settings.calculations ?? [])
        .reduce<Array<{ id: string; title: string }>>(
          (acc, calculation) =>
            (calculation.measurements?.length ?? 0) > 0
              ? [
                  ...acc,
                  {
                    id: calculation.id,
                    title: calculation.name,
                  },
                ]
              : acc,
          []
        )
        .sort((a, b) => a.title.localeCompare(b.title)),
      observations: glossaryData.observations
        .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      samples: glossaryData.samples
        .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      treatments: glossaryData.treatments
        .map(({ api_id, treatment_label }) => ({ id: api_id, title: treatment_label }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    };

    openModal('ADD_TASK', {
      studyId: study.id,
      studyApiId: study.api_id,
      studyStartedOn: study.started_on,
      execution,
      handleSubmit: handleTaskSpecCreate,
    });
  };

  const handleTaskSpecClick = async (event: MouseEvent<HTMLAnchorElement>, taskSpecId: TaskSpecApiId) => {
    event.preventDefault();
    const response = await ApiService.call({
      endpoint: 'GET /api/v1/studies/{studyId}/task-specs/{taskSpecId}',
      path: {
        studyId,
        taskSpecId,
      },
    });
    if (response.type === 'success') {
      const execution: AddTaskProps['execution'] = {
        measurements: (study.settings.calculations ?? [])
          .reduce<Array<{ id: string; title: string }>>(
            (acc, calculation) =>
              (calculation.measurements?.length ?? 0) > 0
                ? [
                    ...acc,
                    {
                      id: calculation.id,
                      title: calculation.name,
                    },
                  ]
                : acc,
            []
          )
          .sort((a, b) => a.title.localeCompare(b.title)),
        observations: glossaryData.observations
          .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
          .sort((a, b) => a.title.localeCompare(b.title)),
        samples: glossaryData.samples
          .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
          .sort((a, b) => a.title.localeCompare(b.title)),
        treatments: glossaryData.treatments
          .map(({ api_id, treatment_label }) => ({ id: api_id, title: treatment_label }))
          .sort((a, b) => a.title.localeCompare(b.title)),
      };
      openModal('EDIT_TASK', {
        studyApiId: study.api_id,
        studyId: study.id,
        taskSpec: response.body,
        execution,
        disabledSections: ['target'],
        handleSubmit: handleTaskSpecUpdate(taskSpecId),
      });
    }
  };
  const handleTaskSpecDelete = async (taskSpecs: Array<TaskSpec>) => {
    try {
      const result = await Promise.allSettled(
        taskSpecs.map((taskSpec) =>
          ApiService.call({
            endpoint: 'DELETE /api/v1/studies/{studyId}/task-specs/{taskSpecId}',
            path: { studyId: taskSpec.study_id, taskSpecId: taskSpec.id },
            options: { onError: { toast: false } },
          })
        )
      );
      const totalDeleted = result.reduce((acc, { status }) => (status === 'fulfilled' ? (acc += 1) : acc), 0);
      if (totalDeleted === 0) {
        errorToast('Could not delete tasks');
      } else if (totalDeleted < taskSpecs.length) {
        warningToast(`Deleted [${totalDeleted}/${taskSpecs.length}] tasks`);
      } else {
        successToast(`Deleted [${totalDeleted}/${taskSpecs.length}] tasks`);
      }
    } catch (e) {
      errorToast('Could not delete tasks');
    }
    setListApiVersion(new Date().getTime());
  };

  const toggleDeleteTaskSpecModal = (taskSpecs: Array<TaskSpec>) => {
    openModal('CONFIRM_DELETE_TASK_SPEC', {
      closeModal,
      onClick: () => handleTaskSpecDelete(taskSpecs),
    });
  };

  const toggleShiftDatesModal = (taskSpecs: Array<TaskSpec>) => {
    openModal('SHIFT_TASK_DATES', {
      taskSpecs,
      studyId,
      closeModal,
      refetchTableData: setListApiVersion,
    });
  };
  const columns = useMemo<Array<TanStackColumnDef<TaskSpec>>>(
    () => [
      {
        id: 'title',
        header: 'Title',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => (
          <Link
            className={classNames('', { 'link blue': !studyInactive, 'pointer-events-none': studyInactive })}
            onClick={(e) => handleTaskSpecClick(e, original.id)}
          >
            {original.title ?? '-'}
          </Link>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => (
          <p className="truncate">{original.description ?? '-'}</p>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => taskTypes[original.type],
      },
      {
        id: 'target',
        header: 'Target',
        size: 75,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) =>
          original.target?.type === 'group' ? (
            <span
              onClick={() =>
                openModal('TASK_TARGET_DETAILS', {
                  target: original.target,
                  groups: studyGroups,
                  studyId,
                  taskTitle: original.title,
                })
              }
              className="link blue"
            >
              {getTargetNameForTasks(original.target)}
            </span>
          ) : (
            <span>{getTargetNameForTasks(original.target)}</span>
          ),
      },
      {
        id: 'frequency',
        header: 'Frequency',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => (
          <TaskScheduleFrequencyCell schedule={original.schedule} />
        ),
      },
      {
        id: 'start',
        header: 'Start',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) =>
          original.schedule.type === 'conditional'
            ? '-'
            : DateUtils.renderDateTime(original.schedule.duration.start, {
                format: DateRenderFormat.Date,
                timezone: original.schedule.timezone ?? undefined,
              }),
      },
      {
        id: 'end',
        header: 'End',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) =>
          original.schedule.type === 'conditional'
            ? '-'
            : DateUtils.renderDateTime(original.schedule.duration.end, {
                format: DateRenderFormat.Date,
                timezone: original.schedule.timezone ?? undefined,
              }),
      },
      {
        id: 'time',
        header: 'Time',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => (
          <TaskSpecTimeCell time={original.schedule.time} />
        ),
      },
      {
        id: 'timezone',
        header: 'Timezone',
        size: 100,
        isVisible: false,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original.schedule.timezone ? (DateUtils.timezoneName(null, original.schedule.timezone) ?? '-') : '-',
      },
      {
        id: 'assignees',
        header: 'Assignees',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpec } }) => (
          <div>
            {(original.assignees ?? []).map((assignee) => (
              <UserAvatar
                key={assignee.id}
                user={{ name: assignee.name }}
                style={{ marginRight: '3px' }}
                tooltip={assignee.name}
              />
            ))}
          </div>
        ),
      },
    ],
    [studyInactive, handleTaskSpecClick, studyGroups]
  );

  const bulkAction = ({ useTableProps: { selectedFlatRows } }: UseTableProps<TaskSpec>): Array<unknown> => {
    const taskSpecs = selectedFlatRows.map(({ original }) => original);
    return [
      {
        name: 'Shift dates',
        key: 'date-shift',
        disabled: selectedFlatRows.some((row) => row.original.schedule.type === 'conditional'),
        tooltip: 'Shift dates can only be performed on recurring and one off tasks',
        action: () => toggleShiftDatesModal(taskSpecs),
      },
      {
        name: 'Delete',
        key: 'delete',
        className: 'red',
        disabled: _isEmpty(selectedFlatRows) || studyInactive,
        action: () => toggleDeleteTaskSpecModal(taskSpecs),
      },
    ];
  };

  return (
    <div>
      <APITable
        columns={columns}
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/studies/{studyId}/task-specs',
            path: { studyId },
          }).url,
        }}
        reduxTableName="studyTasks"
        refetch={listApiVersion}
        bulkActions={!isClosed(study) ? bulkAction : undefined}
        AsideComponent={
          createTaskEnabled
            ? () => (
                <Button testKey="add-task" testId="add-task-button" onClick={handleAddClick}>
                  Add Task
                </Button>
              )
            : undefined
        }
        noDataMessage="Your tasks will appear here"
      />
      {studyExecutionTimezoneEnabled ? (
        <StudyExecutionTimezone
          study={study}
          onTimezoneChange={() => {
            setListApiVersion(Date.now());
            void reloadStudy?.();
          }}
        />
      ) : null}
    </div>
  );
};
